import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';

const navItems = [
  { to: '/', label: '首页', end: true },
  { to: '/category/movie', label: '电影' },
  { to: '/category/tv', label: '电视剧' },
  { to: '/news', label: '资讯' },
  { to: '/about', label: '关于' },
];

export default function Header() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setMenuOpen(false);
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur border-b border-background-300/60'
          : 'bg-gradient-to-b from-background-50/90 to-transparent'
      }`}
    >
      <div className="flex items-center justify-between gap-4 px-4 md:px-6 lg:px-10 h-16 md:h-20">
        <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
          <span className="w-10 h-10 rounded-lg bg-primary-500 flex items-center justify-center text-background-50 text-xl group-hover:bg-primary-600 transition-colors">
            <i className="ri-movie-2-fill" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="text-lg md:text-xl font-bold tracking-wide text-foreground-950 font-heading">
              红桃影视
            </span>
            <span className="text-[10px] tracking-[0.28em] text-foreground-500 font-display">
              HONGTAO TV
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive
                    ? 'text-primary-400 bg-primary-500/10'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-200'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="hidden md:flex items-center gap-2 w-60 lg:w-72">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视或资讯"
              className="w-full h-10 pl-9 pr-3 rounded-md bg-background-200 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors"
            />
          </div>
          <button
            type="submit"
            className="h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            搜索
          </button>
        </form>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="打开菜单"
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-900 bg-background-200 text-xl cursor-pointer"
        >
          <i className={menuOpen ? 'ri-close-line' : 'ri-menu-line'} />
        </button>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-300/60 bg-background-100 px-4 py-4 space-y-3 animate-fade-in">
          <form onSubmit={submitSearch} className="flex items-center gap-2">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视或资讯"
                className="w-full h-10 pl-9 pr-3 rounded-md bg-background-200 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>
          <nav className="grid grid-cols-2 gap-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-md text-sm font-medium text-center transition-colors ${
                    isActive
                      ? 'bg-primary-500/15 text-primary-300'
                      : 'bg-background-200 text-foreground-700'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}